import classes from './RowGroup.module.css';
import classNames from 'classnames';
import * as React from 'react';
import { COLLAPSED_ROW_HEIGHT } from '../../layout';
import { useExpandedRows, useOrganizations, useRowHeights, useTasksByOrganization } from '../../state/context';
import { FiChevronDown, FiImage } from 'react-icons/fi';
import { pluralize } from '../../utils/stringUtils';
import { Organization, Task } from '../../types';

type OrganizationSidebarRowProps = {
  organization: Organization;
  tasks: Task[];
  height: number;
  onToggle: (id: number) => void;
};

const OrganizationSidebarRow = ({
  tasks,
  organization,
  height,
  onToggle,
}: OrganizationSidebarRowProps) => {
  const [expandedRows] = useExpandedRows();
  const isExpanded = expandedRows.includes(organization.id);
  const numberOfSolutions = React.useMemo(() => {
    return tasks
      .map((task) => task.solutionId)
      .filter((value, index, self) => self.indexOf(value) === index)
      .length;
  }, [tasks]);

  return (
    <div
      key={organization.id}
      style={{ height }}
      className={classNames(
        classes.rowGroup,
        'flex flex-col items-center justify-center transition-height'
      )}
    >
      <div className="overflow-hidden py-10 h-full text-center">
        <div>
          <div className="h-16 flex items-center justify-center">
            {organization.logoUrl && (
              <div
                style={{
                  width: '135px',
                  height: '45px',
                }}
              >
                <img src={organization.logoUrl} alt={organization.name} className="block w-full h-full object-contain" />
              </div>
            )}
            {!organization.logoUrl && (
              <div className="w-8 h-8 bg-gray-200 rounded-md flex items-center justify-center mx-auto">
                <FiImage className="w-5 h-5 my-1 opacity-50" />
              </div>
            )}
          </div>

          <div className="mt-7">
            <div className="text-3xl font-light">{tasks.length}</div>
            <div className="text-xxs uppercase font-bold">
              {pluralize(tasks.length, 'sag', 'sager')}
            </div>
            <div className="text-xxs uppercase font-thin">I alt over hele perioden</div>
          </div>

          <div className="mt-4">
            <div className="text-3xl font-light">{numberOfSolutions}</div>
            <div className="text-xxs uppercase font-bold">
              {pluralize(numberOfSolutions, 'applikation', 'applikationer')}
            </div>
            <div className="text-xxs uppercase font-thin">Fordeler sagerne sig på</div>
          </div>

          <button
            className={classes['tab']}
            onClick={() => onToggle(organization.id)}
          >
            {isExpanded ? 'Vis mindre' : 'Vis mere'} <FiChevronDown className={classNames(isExpanded ? 'rotate-180' : '')} />
          </button>
        </div>
      </div>
    </div>
  );
};

const OrganizationSidebarRows = () => {
  const organizations = useOrganizations();
  const rowHeights = useRowHeights();
  const tasks = useTasksByOrganization();
  const [_, toggleRow] = useExpandedRows();

  return (
    <>
      {organizations.map((organization) => (
        <OrganizationSidebarRow
          key={organization.id}
          organization={organization}
          tasks={tasks[organization.id] || []}
          height={rowHeights[organization.id] || COLLAPSED_ROW_HEIGHT}
          onToggle={toggleRow}
        />
      ))}
    </>
  );
};

export default OrganizationSidebarRows;
